/**
 * C-GTCC-COCKPIT-INFRA1 — typed read client for the GTCC fleet endpoints.
 *
 * The shapes below are the data contract of G-GTCC-API-FLEET1, transcribed
 * from the instruction body without addition: the same five GETs, the same
 * field names, the same nullability. G owns the server side; until G is
 * merged the tab renders fixtures (`../fixtures/fleet.*.json`) that carry the
 * values of `GTCC_REFERENCE_2026-09-01.html`. After G merges this module is
 * rebased onto the real DTO type instead of re-declaring it.
 *
 * Read-only by construction: `apiGet` only, no mutation verb, no upstream URL
 * and no PromQL — exactly like `apps/admin/src/monitoring/api.ts:74`.
 *
 * Freshness and thresholds are never computed here. `status`, `ageSeconds`,
 * `freshnessSeconds`, `verdict` and `daysToFull` arrive decided by the API
 * (Owner decisions P8/P9/P10: a stale source is never shown as healthy, an
 * unmeasurable value is "nicht messbar" and never an invented number).
 */
import { apiGet, ApiError } from "../../lib/api.js";

/* --------------------------------------------------------------- paths */

const FLEET_BASE = "/v1/admin/gtcc/fleet";

export const FLEET_HOSTS_PATH = `${FLEET_BASE}/hosts`;
export const FLEET_STORAGE_PATH = `${FLEET_BASE}/storage`;
export const FLEET_TLS_PATH = `${FLEET_BASE}/tls`;
export const FLEET_SUMMARY_PATH = `${FLEET_BASE}/summary`;

/** `/hosts/:host` — the host name is the only path segment this module appends. */
export function fleetHostDetailPath(host: string): string {
  return `${FLEET_HOSTS_PATH}/${encodeURIComponent(host)}`;
}

/* ---------------------------------------------------------------- types */

/** The closed status enum of the GTCC API convention. */
export const FLEET_STATUSES = ["healthy", "warning", "critical", "stale", "unmeasurable"] as const;
export type FleetStatus = (typeof FLEET_STATUSES)[number];

/** Per-signal verdict of the host detail table. */
export const FLEET_VERDICTS = ["ok", "warn", "crit", "info", "unmeasurable"] as const;
export type FleetVerdict = (typeof FLEET_VERDICTS)[number];

export interface FleetDiskDto {
  readonly usedPercent: number;
  readonly usedBytes: number;
  readonly freeBytes: number;
  /** `null` = no forecast measurable; never replaced by a guess. */
  readonly daysToFull: number | null;
}

/**
 * A remote repository attached to a host (Storage Box). It is carried as its
 * own object and rendered as its own row — a REMOTE target never appears as a
 * local disk (GT-OPEN-123).
 */
export interface FleetRemoteDto {
  readonly label: string;
  readonly usedPercent: number | null;
  readonly capacityBytes: number | null;
  readonly measurable: boolean;
}

export interface FleetHostDto {
  readonly host: string;
  readonly role: string;
  readonly status: FleetStatus;
  readonly measuredAt: string;
  readonly ageSeconds: number;
  readonly latencyMs: number | null;
  /** 12 values over 24 h; may be shorter when the source went stale. */
  readonly latencySpark: ReadonlyArray<number>;
  readonly cpuPercent: number | null;
  readonly vcpu: number | null;
  readonly memPercent: number | null;
  readonly memFreeBytes: number | null;
  readonly disk: FleetDiskDto | null;
  readonly netInBps: number | null;
  readonly netOutBps: number | null;
  readonly remote: FleetRemoteDto | null;
}

export interface FleetHostsDto {
  readonly measuredAt: string;
  readonly freshnessSeconds: number;
  readonly status: FleetStatus;
  readonly hosts: ReadonlyArray<FleetHostDto>;
}

export interface FleetSignalDto {
  readonly key: string;
  readonly label: string;
  readonly valueText: string;
  readonly verdict: FleetVerdict;
  readonly verdictText: string;
}

export interface FleetHostDetailDto {
  readonly measuredAt: string;
  /**
   * The GTCC API convention (`GTCC_1ZU1_PROGRAM.md`, "API-Konventionen") gives
   * every response a `freshnessSeconds`; the per-endpoint sketch of the
   * C-instruction spells it out only for `/hosts`. It is therefore optional
   * here: when it arrives the panel shows the age badge, otherwise it shows
   * the measurement timestamp — never a locally computed age.
   */
  readonly freshnessSeconds?: number;
  readonly status: FleetStatus;
  readonly signals: ReadonlyArray<FleetSignalDto>;
}

export type FleetStorageKind = "local" | "remote" | "wal";

export interface FleetStorageRowDto {
  readonly host: string;
  readonly label: string;
  readonly kind: FleetStorageKind;
  readonly usedPercent: number | null;
  readonly freeBytes: number | null;
  readonly note: string;
  readonly measurable: boolean;
}

export interface FleetStorageDto {
  readonly measuredAt: string;
  /** Optional for the reason documented on `FleetHostDetailDto`. */
  readonly freshnessSeconds?: number;
  readonly status: FleetStatus;
  readonly rows: ReadonlyArray<FleetStorageRowDto>;
}

export interface FleetCertDto {
  readonly domain: string;
  readonly daysRemaining: number | null;
  readonly status: FleetStatus;
}

export interface FleetTlsDto {
  readonly measuredAt: string;
  /** Optional for the reason documented on `FleetHostDetailDto`. */
  readonly freshnessSeconds?: number;
  readonly status: FleetStatus;
  readonly certs: ReadonlyArray<FleetCertDto>;
}

export interface FleetSummaryDto {
  readonly measuredAt: string;
  /** Optional for the reason documented on `FleetHostDetailDto`. */
  readonly freshnessSeconds?: number;
  readonly status: FleetStatus;
  readonly hostsHealthy: number;
  readonly hostsTotal: number;
  readonly warnings: ReadonlyArray<string>;
  readonly driftHosts: number;
  readonly ntpMaxSkewMs: number | null;
  /**
   * C-GTCC-VIEW-OVW1: the "VPS Uptime (30 T)" row of Operations Overview reads
   * its value here — the instruction names `fleet/summary` (G-GTCC-API-FLEET1)
   * as the source for "Hosts, Uptime", and the INFRA1 transcription had no
   * uptime field because the Infrastructure tab does not print one. Optional,
   * so a payload of the shape INFRA1 declared stays valid; `null` and absent
   * both mean the row reads "nicht messbar".
   */
  readonly uptimePercent30d?: number | null;
}

/* --------------------------------------------------------------- result */

/**
 * Same discriminated union as the monitoring client
 * (`apps/admin/src/monitoring/api.ts:34`): the tab never sees `ApiError`.
 * `disabled` is the honest answer while G-GTCC-API-FLEET1 is unmerged — the
 * route answers 404 and the panels say so instead of inventing numbers.
 */
export type FleetResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly kind: "disabled"; readonly httpStatus: 404 }
  | { readonly ok: false; readonly kind: "forbidden"; readonly httpStatus: 403 }
  | {
      readonly ok: false;
      readonly kind: "upstream";
      readonly httpStatus: number;
      readonly requestId: string | undefined;
    }
  | { readonly ok: false; readonly kind: "transport_error"; readonly message: string };

async function fetchFleet<T>(path: string, signal?: AbortSignal): Promise<FleetResult<T>> {
  try {
    return { ok: true, data: await apiGet<T>(path, undefined, signal) };
  } catch (err) {
    if (err instanceof ApiError) {
      const { status, requestId } = err;
      if (status === 404) return { ok: false, kind: "disabled", httpStatus: 404 };
      if (status === 403) return { ok: false, kind: "forbidden", httpStatus: 403 };
      return { ok: false, kind: "upstream", httpStatus: status, requestId };
    }
    const message = err instanceof Error ? err.message : "gtcc fleet fetch failed";
    return { ok: false, kind: "transport_error", message };
  }
}

export function fetchFleetHosts(signal?: AbortSignal): Promise<FleetResult<FleetHostsDto>> {
  return fetchFleet<FleetHostsDto>(FLEET_HOSTS_PATH, signal);
}

export function fetchFleetHostDetail(
  host: string,
  signal?: AbortSignal,
): Promise<FleetResult<FleetHostDetailDto>> {
  return fetchFleet<FleetHostDetailDto>(fleetHostDetailPath(host), signal);
}

export function fetchFleetStorage(signal?: AbortSignal): Promise<FleetResult<FleetStorageDto>> {
  return fetchFleet<FleetStorageDto>(FLEET_STORAGE_PATH, signal);
}

export function fetchFleetTls(signal?: AbortSignal): Promise<FleetResult<FleetTlsDto>> {
  return fetchFleet<FleetTlsDto>(FLEET_TLS_PATH, signal);
}

export function fetchFleetSummary(signal?: AbortSignal): Promise<FleetResult<FleetSummaryDto>> {
  return fetchFleet<FleetSummaryDto>(FLEET_SUMMARY_PATH, signal);
}

/* ----------------------------------------------------------- query keys */

/*
 * Prefix convention of the GTCC query-key inventory: `["gtcc", <viewkey>, …]`.
 *
 * Each key is an own exported array literal, not only a field of the object
 * below: A-GTCC-QUERY-KEY-CONSISTENCY1 reads the keys statically out of the
 * source, and it can follow `queryKey: FLEET_HOSTS_KEY` to this literal, while
 * a member access `FLEET_QUERY_KEYS.hosts` stays unreadable for it. The object
 * remains as the grouped form for callers that want all of them.
 */
export const FLEET_HOSTS_KEY = ["gtcc", "cockpit", "fleet-hosts"] as const;
export const FLEET_HOST_DETAIL_KEY = ["gtcc", "cockpit", "fleet-host"] as const;
export const FLEET_STORAGE_KEY = ["gtcc", "cockpit", "fleet-storage"] as const;
export const FLEET_TLS_KEY = ["gtcc", "cockpit", "fleet-tls"] as const;
export const FLEET_SUMMARY_KEY = ["gtcc", "cockpit", "fleet-summary"] as const;

/**
 * C-GTCC-VIEW-OVW1: the same endpoint read from Operations Overview. Its own
 * key because the view boundary cancels and clears by prefix — `ovw` fetches
 * under `["gtcc", "overview"]` (`registry.ts:177`), so a cockpit-prefixed key
 * would survive the boundary of a view it does not belong to.
 */
export const FLEET_SUMMARY_OVERVIEW_KEY = ["gtcc", "overview", "fleet-summary"] as const;

export const FLEET_QUERY_KEYS = {
  hosts: FLEET_HOSTS_KEY,
  hostDetail: (host: string) => [...FLEET_HOST_DETAIL_KEY, host] as const,
  storage: FLEET_STORAGE_KEY,
  tls: FLEET_TLS_KEY,
  summary: FLEET_SUMMARY_KEY,
  summaryForOverview: FLEET_SUMMARY_OVERVIEW_KEY,
} as const;
